import type { InkAttack } from '../../../data/full/tatoue';
import { angleOf, rotate, unitFromAngle } from '../../../shared/angle';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy, keepWhere } from '../../effects';
import { touchesObstacle } from '../../obstacles';
import type { PlayerState, PropState, SimState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { ownerOf } from '../../props';

export const DRAGON = 'dragon';

const LANES = [0, -1, 1] as const;
const FAN_RADIANS = 0.18;

export const ink: AttackModule<InkAttack> = {
  reads: [
    'attackCooldownMul',
    'damageMul',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step({ state }, player, attack, input) {
    if (!pullTrigger(player, input.fire, attack.cooldownTicks)) {
      return;
    }
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    const speed = statValue(player, 'projectileSpeedMul', attack.speed);
    const damage = statValue(player, 'damageMul', attack.damage);
    const walls = Math.max(0, Math.floor(statValue(player, 'pierceAdd', 0)));
    let rank = nextRank(state, player);
    for (let index = 0; index < count; index++) {
      const turn = (index - (count - 1) / 2) * FAN_RADIANS;
      const way = turn === 0 ? player.aim : rotate(player.aim, turn);
      const dragon: PropState = {
        id: state.nextEntityId,
        kind: DRAGON,
        playerId: player.id,
        phase: 'flying',
        x: player.x,
        y: player.y,
        prevX: player.x,
        prevY: player.y,
        vx: way.x * speed,
        vy: way.y * speed,
        damage,
        ticksLeft: attack.rangeTicks,
        // It goes through every bad vibe: pierce lets it fly through obstacles instead.
        hitsLeft: walls,
        hitIds: [],
        rank,
      };
      state.nextEntityId += 1;
      rank += 1;
      (state.props ??= []).push(dragon);
    }
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (!props?.some((prop) => prop.kind === DRAGON)) {
      return;
    }
    ctx.enemyGrid.rebuild(state.enemies);
    let gone = false;
    for (const prop of props) {
      if (prop.kind === DRAGON) {
        fly(ctx, prop);
        gone ||= prop.ticksLeft <= 0;
      }
    }
    if (gone) {
      keepWhere(props, (prop) => prop.kind !== DRAGON || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.speed) * attack.rangeTicks;
  },
};

function inkOffset(attack: InkAttack, rank: number, age: number): number {
  const lane = LANES[rank % LANES.length] ?? 0;
  const phase = (2 * Math.PI * age) / attack.waveTicks + rank;
  const wave = lane * attack.lane + attack.amplitude * unitFromAngle(phase).y;
  return wave * Math.min(1, age / attack.laneTicks);
}

function nextRank(state: SimState, player: PlayerState): number {
  let next = 0;
  for (const prop of state.props ?? []) {
    if (prop.kind === DRAGON && prop.playerId === player.id) {
      next = Math.max(next, (prop.rank ?? 0) + 1);
    }
  }
  return next;
}

function attackOf(ctx: StepContext, owner: PlayerState): InkAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'ink') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" lets out no ink`);
  }
  return attack;
}

function fly(ctx: StepContext, dragon: PropState): void {
  const { state, enemyGrid, obstacles } = ctx;
  const attack = attackOf(ctx, ownerOf(state, dragon));
  const rank = dragon.rank ?? 0;
  const age = attack.rangeTicks - dragon.ticksLeft;
  const side = inkOffset(attack, rank, age + 1) - inkOffset(attack, rank, age);
  const speed = Math.sqrt(dragon.vx * dragon.vx + dragon.vy * dragon.vy);
  const inside =
    age > 0 && touchesObstacle({ x: dragon.x, y: dragon.y, radius: attack.radius }, obstacles);
  dragon.x += dragon.vx - (dragon.vy / speed) * side;
  dragon.y += dragon.vy + (dragon.vx / speed) * side;
  dragon.ticksLeft -= 1;
  const hitIds = (dragon.hitIds ??= []);
  const found = enemyGrid.queryInListOrder(dragon.x, dragon.y, attack.radius);
  for (let i = 0; i < found; i++) {
    const enemy = state.enemies[enemyGrid.result(i)];
    if (enemy !== undefined && enemy.hp > 0 && !hitIds.includes(enemy.id)) {
      hitIds.push(enemy.id);
      hurtEnemy(state, enemy, dragon.damage, dragon.playerId, {
        x: dragon.prevX,
        y: dragon.prevY,
      });
    }
  }
  const { width, height } = state.arena;
  const out = dragon.x < 0 || dragon.x > width || dragon.y < 0 || dragon.y > height;
  const blocked = touchesObstacle({ x: dragon.x, y: dragon.y, radius: attack.radius }, obstacles);
  if (blocked && !inside) {
    dragon.hitsLeft -= 1;
  }
  if (out || (blocked && dragon.hitsLeft < 0)) {
    dragon.ticksLeft = 0;
  }
}
